import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import { fetchMetaDriveFiles, type MetaDriveFile } from '../../api/meta';
import { apiFetch, ApiError } from '../../api/client';

function importDriveFile(fileId: string): Promise<{ asset: { metaImageHash: string | null; metaVideoId: string | null } }> {
  return apiFetch('/api/meta/drive/import', { method: 'POST', body: { fileId } });
}

function sizeLabel(bytes: number | null): string {
  if (!bytes) return '—';
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function ImportCell({ file }: { file: MetaDriveFile }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => importDriveFile(file.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meta-drive-files'] }),
  });

  if (file.imported) return <Chip size="small" color="success" variant="outlined" label="W bibliotece Meta" />;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        size="small"
        variant="outlined"
        startIcon={mutation.isPending ? <CircularProgress size={14} color="inherit" /> : <CloudUploadRoundedIcon fontSize="small" />}
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? 'Importuję…' : 'Importuj do Meta'}
      </Button>
      {mutation.isError && (
        <Typography variant="caption" color="error">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Błąd importu'}
        </Typography>
      )}
    </Stack>
  );
}

export function MetaDrivePanel() {
  const query = useQuery({
    queryKey: ['meta-drive-files'],
    queryFn: ({ signal }) => fetchMetaDriveFiles(signal),
    retry: false,
  });

  const folderId = query.data?.folderId ?? null;
  const files = query.data?.files ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Kreacje (Google Drive)</Typography>
          <Typography variant="body2" color="text.secondary">
            Pliki z folderu zespołu — do użycia w kreatorze kampanii; import wgrywa plik do biblioteki mediów Meta
          </Typography>
        </Box>
        {folderId && (
          <Link href={`https://drive.google.com/drive/folders/${folderId}`} target="_blank" rel="noreferrer" variant="body2">
            Otwórz folder w Drive
          </Link>
        )}
      </Stack>

      {query.isLoading && (
        <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
      )}

      {query.isError && (
        <Alert severity="warning">
          Google Drive niedostępny: {query.error instanceof ApiError ? query.error.message : 'błąd połączenia'} — sprawdź,
          czy folder jest udostępniony kontu serwisowemu i ustawiony w konfiguracji.
        </Alert>
      )}

      {query.isSuccess && !folderId && (
        <Alert severity="info">Folder Drive nie jest jeszcze skonfigurowany.</Alert>
      )}

      {query.isSuccess && folderId && files.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Folder jest udostępniony, ale nie znaleziono w nim plików graficznych ani wideo.
        </Typography>
      )}

      {files.length > 0 && (
        <TableContainer sx={{ maxHeight: 380 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Plik</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell align="right">Rozmiar</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f.id} hover>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {f.kind === 'video' ? <VideocamRoundedIcon fontSize="small" color="action" /> : <ImageRoundedIcon fontSize="small" color="action" />}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{f.name}</Typography>
                        {f.path && <Typography variant="caption" color="text.secondary" noWrap display="block">{f.path}</Typography>}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={f.kind} /></TableCell>
                  <TableCell align="right">{sizeLabel(f.sizeBytes)}</TableCell>
                  <TableCell><ImportCell file={f} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
